/**
 * Language picker — native-script labels with a check on the selection,
 * optimistic PATCH with revert. Wired: PATCH /api/account/profile {language}.
 * Port of screens-account.jsx LanguagePicker (language list per brief).
 */
import React from 'react';
import { Pressable, View } from 'react-native';

import { space } from '@bero/tokens';
import { goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Hairline, Screen, TopBar } from '@/ui';

// native = endonym (always shown in its OWN script, never translated).
// en = English exonym for reference. ready = has a reviewed dictionary today;
// the rest are listed but marked "coming soon" so nobody is dropped into an
// all-English fallback thinking they picked their language.
const LANGS: Array<{ native: string; en: string; code: string; ready: boolean }> = [
  { native: 'English', en: 'English', code: 'en', ready: true },
  { native: 'हिन्दी', en: 'Hindi', code: 'hi', ready: true },
  { native: 'मराठी', en: 'Marathi', code: 'mr', ready: true },
  { native: 'தமிழ்', en: 'Tamil', code: 'ta', ready: false },
  { native: 'తెలుగు', en: 'Telugu', code: 'te', ready: false },
  { native: 'ಕನ್ನಡ', en: 'Kannada', code: 'kn', ready: false },
];

function initialCode(language: string | undefined): string {
  const l = (language ?? '').toLowerCase();
  const hit = LANGS.find((x) => x.code === l || x.en.toLowerCase() === l);
  return hit ? hit.code : 'en';
}

export default function LanguageScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { user, refresh } = useSession();
  // Derive the highlighted row from the saved language (which loads after first
  // render), overlaying only an optimistic pick while a PATCH is in flight — no
  // sync effect needed, so the check always follows the user's ACTUAL language.
  const [override, setOverride] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const sel = override ?? initialCode(user?.language);

  const choose = (code: string) => {
    if (code === sel) return;
    setOverride(code); // optimistic
    setNotice(null);
    api.patch<unknown>('/api/account/profile', { language: code })
      .then(() => { void refresh(); }) // server now matches the override
      .catch(() => {
        setOverride(null); // revert to the saved language
        setNotice(t('lang_error'));
      });
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('lang_title')} onBack={goBack} />
      <Screen style={{ paddingTop: space.s2 }}>
        {notice ? (
          <AppText variant="bodySm" tone="danger" style={{ paddingHorizontal: space.s4, paddingBottom: space.s2 }}>
            {notice}
          </AppText>
        ) : null}
        {LANGS.map(({ native, en, code, ready }) => (
          <View key={code}>
            <Pressable
              accessibilityRole="button"
              accessibilityState={{ selected: sel === code, disabled: !ready }}
              disabled={!ready}
              onPress={() => choose(code)}
              style={({ pressed }) => ({
                flexDirection: 'row', alignItems: 'center', gap: space.s2,
                paddingVertical: 14, paddingHorizontal: space.s4, minHeight: 48,
                opacity: ready ? 1 : 0.5,
                backgroundColor: pressed && ready ? colors.canvas1 : 'transparent',
              })}
            >
              <View style={{ flex: 1, flexDirection: 'row', alignItems: 'baseline', gap: 6 }}>
                <AppText variant="bodyMd">{native}</AppText>
                {en !== native ? <AppText variant="bodySm" tone="tertiary">· {en}</AppText> : null}
              </View>
              {!ready ? (
                <AppText variant="label" tone="tertiary">{t('lang_soon')}</AppText>
              ) : sel === code ? (
                <Icon name="check" size={20} color={colors.primary500} />
              ) : null}
            </Pressable>
            <Hairline inset={16} />
          </View>
        ))}
        <AppText variant="bodySm" tone="tertiary" style={{ paddingHorizontal: space.s4, paddingTop: space.s3, textAlign: 'center' }}>
          {t('lang_footer')}
        </AppText>
      </Screen>
    </Screen>
  );
}
